import { differenceInHours } from 'date-fns';
import { z } from 'zod';
import { LessonParticipants, LessonTypesEnum } from 'types/generated/client';
import { VideoCardProps } from './props';

// Utility: Generate mock trend data for charts
export const generateTrendData = (isUpward: boolean): { value: number }[] => {
  const data = [];
  let value = 50;
  for (let i = 0; i < 20; i++) {
    value += (Math.random() - 0.5) * 10 + (isUpward ? 1 : -1) * 0.5;
    data.push({ value: Math.max(0, value) });
  }
  return data;
};

// Utility: Calculate date variables for dashboard queries
export const getDateVariables = () => {
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 1);

  return {
    currentMonthStart: currentMonthStart.toISOString(),
    previousMonthStart: previousMonthStart.toISOString(),
    previousMonthEnd: previousMonthEnd.toISOString(),
  };
};

// Utility: Group and count lessons per player
export const groupByPlayer = (lessonParticipants: LessonParticipants[] = []) => {
  const map = new Map<
    string,
    {
      id: string;
      fullName: string;
      profileImagePath: string;
      lessonCount: number;
    }
  >();
  lessonParticipants.forEach(({ userId, userProfile }) => {
    if (!userId || !userProfile) return;
    if (!map.has(userId)) {
      map.set(userId, {
        id: userId,
        fullName: userProfile.fullName || '',
        profileImagePath: userProfile.profileImagePath || '',
        lessonCount: 0,
      });
    }
    map.get(userId)!.lessonCount += 1;
  });
  return Array.from(map.values()).sort((a, b) => b.lessonCount - a.lessonCount);
};

// Utility: Format time string
export const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Utility: Calculate time until lesson
export const getTimeUntilLesson = (startDateTime: string) => {
  const now = new Date();
  const lessonStart = new Date(startDateTime);
  const diffInMs = lessonStart.getTime() - now.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffInHours > 0) {
    if (diffInMinutes > 0) {
      return `In ${diffInHours} hr ${diffInMinutes}m`;
    }
    return `In ${diffInHours} hr`;
  } else if (diffInMinutes > 0) {
    return `In ${diffInMinutes}m`;
  }
  return 'Now';
};

// Utility: Get lesson type display label
export const getLessonTypeDisplayLabel = (lessonType: LessonTypesEnum, typeCustom?: string) => {
  if (typeCustom) return typeCustom;
  switch (lessonType) {
    case LessonTypesEnum.Individual:
      return 'Individual lesson';
    case LessonTypesEnum.Clinic:
      return 'Clinic';
    case LessonTypesEnum.Cardio:
      return 'Cardio';
    case LessonTypesEnum.Camp:
      return 'Camp';
    case LessonTypesEnum.Retreat:
      return 'Retreat';
    case LessonTypesEnum.Custom:
      return 'Custom';
    default:
      return 'Lesson';
  }
};

// Utility: Get location display
export const getLocationDisplay = (lesson: any) => {
  if (lesson.venue?.addressString) {
    return lesson.venue.addressString;
  }
  if (lesson.userCustomCourt?.fullAddress) {
    return lesson.userCustomCourt.fullAddress;
  }
  return 'N/A';
};

// Utility: Get capacity display
export const getCapacityDisplay = (lesson: any) => {
  const currentCount = lesson.participantsAggregate?.aggregate?.count || 0;
  const limit = lesson.participantLimit;
  if (limit && limit > 1) {
    return `${currentCount}/${limit}`;
  }
  return currentCount > 0 ? 'Full' : 'Available';
};

// Utility: Get row class name for lesson type
export const getRowClassName = (lessonType: LessonTypesEnum) => {
  switch (lessonType) {
    case LessonTypesEnum.Individual:
      return 'bg-[rgba(225,255,127,0.3)] border-[#e1ff7f]';
    case LessonTypesEnum.Clinic:
      return 'bg-[rgba(179,236,255,0.3)] border-[#b3ecff]';
    case LessonTypesEnum.Cardio:
      return 'bg-[rgba(214,201,255,0.3)] border-[#d6c9ff]';
    case LessonTypesEnum.Camp:
      return 'bg-[rgba(34,197,94,0.3)] border-[#22c55e]';
    case LessonTypesEnum.Retreat:
      return 'bg-[rgba(168,85,247,0.3)] border-[#a855f7]';
    case LessonTypesEnum.Custom:
      return 'bg-[rgba(239,68,68,0.3)] border-[#ef4444]';
    default:
      return 'bg-white';
  }
};

// Utility: Format lesson date
export const formatLessonDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Utility: Get difference in hours between now and a date string
export const getDifferenceInHours = (date: string) => {
  const hoursDifference = differenceInHours(new Date(), new Date(date));
  return hoursDifference;
};

// Zod schema for URL validation - matches API route validation logic
export const uploadReviewsSchema = z.object({
  links: z
    .array(
      z.object({
        url: z
          .string()
          .min(1, 'URL is required')
          .refine((url) => {
            try {
              new URL(url);
              return true;
            } catch {
              return false;
            }
          }, 'Please enter a valid URL'),
      }),
    )
    .min(1, 'At least one URL is required'),
});

const getYouTubeThumbnail = (youtubeUrl: string): string => {
  const videoId = youtubeUrl.match(/(?:embed\/|v=|\/)([a-zA-Z0-9_-]{11})/)?.[1];
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  }
  return '/images/coaches/ryan-on-ball.png'; // fallback image
};

export const youtubeVideos: VideoCardProps[] = [
  {
    imageSrc: getYouTubeThumbnail('https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1'),
    duration: '30:23',
    badges: ['Listing', 'Marketplace'],
    title: 'What do your clients see.',
    timeAgo: '1m ago',
    youtubeUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1',
  },
  {
    imageSrc: getYouTubeThumbnail('https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1'),
    duration: '30:23',
    badges: ['Clinic', 'Marketplace'],
    title: 'Create clinics to show up in the marketplace.',
    timeAgo: '1m ago',
    youtubeUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1',
  },
  {
    imageSrc: getYouTubeThumbnail('https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1'),
    duration: '30:23',
    badges: ['Calendar', 'Schedule'],
    title: 'Connect your calendar.',
    timeAgo: '1m ago',
    youtubeUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1',
  },
  {
    imageSrc: getYouTubeThumbnail('https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1'),
    duration: '30:23',
    badges: ['Clinic', 'Marketplace'],
    title: 'Create your first lesson.',
    timeAgo: '1m ago',
    youtubeUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1',
  },
  {
    imageSrc: getYouTubeThumbnail('https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1'),
    duration: '30:23',
    badges: ['Listing', 'Payments'],
    title: 'Get ratings,reviews, and tips',
    timeAgo: '1m ago',
    youtubeUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?enablejsapi=1',
  },
];
